import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { MapPin, Loader2, Save, Users, Download, ExternalLink, Link2, Megaphone, Send, PartyPopper } from "lucide-react";
import { api, API, formatApiErrorDetail } from "@/lib/api";

const labelCls = "block text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-2";
const inputCls = "w-full border border-slate-300 bg-white px-4 py-3 rounded-none focus:outline-none focus:border-[#D90429] focus:ring-1 focus:ring-[#D90429] transition-colors duration-200";

export const MyChapter = () => {
  const [chapter, setChapter] = useState(null);
  const [members, setMembers] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [announcements, setAnnouncements] = useState([]);
  const [ann, setAnn] = useState({ subject: "", message: "" });
  const [sending, setSending] = useState(false);
  const [stretch, setStretch] = useState("");
  const [raising, setRaising] = useState(false);

  useEffect(() => {
    api.get("/me/chapter")
      .then(({ data }) => {
        setChapter(data);
        setForm({
          name: data.name || "", city: data.city || "",
          contact_email: data.contact_email || "", contact_phone: data.contact_phone || "",
          link: data.link || "", status: data.status || "Forming",
        });
        return Promise.all([api.get("/me/chapter/founding-members"), api.get("/me/chapter/announcements")]);
      })
      .then((res) => {
        if (res) {
          setMembers(res[0].data);
          setAnnouncements(res[1].data);
        }
      })
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  if (!loaded || !chapter) return null;

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/me/chapter", form);
      setChapter(data);
      toast.success("Chapter updated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed");
    } finally {
      setSaving(false);
    }
  };

  const exportCsv = async () => {
    try {
      const r = await api.get("/me/chapter/founding-members.csv", { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${chapter.slug}-founding-members.csv`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30000);
    } catch { toast.error("Export failed"); }
  };

  const shareUrl = `${window.location.origin}/chapter/${chapter.slug}`;
  const copyLink = () => navigator.clipboard.writeText(shareUrl).then(() => toast.success("Link copied")).catch(() => {});

  const raiseGoal = async () => {
    const n = parseInt(stretch, 10);
    if (!n || n <= (chapter.founding_count || 0)) { toast.error(`Enter a goal above your current ${chapter.founding_count} members.`); return; }
    setRaising(true);
    try {
      const { data } = await api.put("/me/chapter", { founding_goal: n });
      setChapter(data);
      setStretch("");
      toast.success(`New stretch goal set: ${n} founding members!`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to set goal");
    } finally { setRaising(false); }
  };

  const sendAnnouncement = async () => {
    if (!ann.subject.trim() || !ann.message.trim()) { toast.error("Add a subject and message."); return; }
    setSending(true);
    try {
      const { data } = await api.post("/me/chapter/announcements", ann);
      toast.success(data.recipient_count > 0 ? `Update sent to ${data.recipient_count} founding member${data.recipient_count !== 1 ? "s" : ""}` : "Update saved (no founding members yet)");
      setAnnouncements((list) => [data.announcement, ...list]);
      setAnn({ subject: "", message: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Send failed");
    } finally {
      setSending(false);
    }
  };

  return (
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="mt-8 bg-white border border-slate-200 p-6 sm:p-8" data-testid="my-chapter">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6 pb-3 border-b-2 border-[#0A1128]">
        <div className="flex items-center gap-3">
          <MapPin className="w-6 h-6 text-[#D90429]" />
          <h2 className="font-display text-2xl uppercase">My Chapter</h2>
        </div>
        <a href={`/chapter/${chapter.slug}`} target="_blank" rel="noopener noreferrer" data-testid="my-chapter-view-page" className="inline-flex items-center gap-1 text-xs uppercase font-bold text-[#0A1128] hover:text-[#D90429]">
          <ExternalLink className="w-3.5 h-3.5" /> View public page
        </a>
      </div>

      {/* Share link */}
      <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 px-3 py-2 mb-6">
        <Link2 className="w-4 h-4 text-slate-400 shrink-0" />
        <span className="text-sm text-slate-500 truncate flex-1">{shareUrl}</span>
        <button data-testid="my-chapter-copy-link" onClick={copyLink} className="text-xs uppercase font-bold text-[#D90429] hover:opacity-70">Copy</button>
      </div>

      {/* Raise-the-goal prompt (shown after a milestone) */}
      {chapter.goal_reached && (
        <div data-testid="raise-goal-banner" className="mb-6 bg-gradient-to-r from-[#D90429]/10 to-[#F4A261]/10 border border-[#F4A261]/40 p-5">
          <div className="flex items-center gap-2 mb-2">
            <PartyPopper className="w-5 h-5 text-[#D90429]" />
            <h3 className="font-display text-lg uppercase">Goal Reached — Aim Higher!</h3>
          </div>
          <p className="text-sm text-slate-600 mb-3">
            Your chapter hit its founding goal of {chapter.founding_goal} with {chapter.founding_count} members. Set a new stretch goal to keep the momentum going.
          </p>
          <div className="flex gap-2 max-w-xs">
            <input data-testid="stretch-goal-input" type="number" min={(chapter.founding_count || 0) + 1} value={stretch} onChange={(e) => setStretch(e.target.value)} placeholder={`e.g. ${(chapter.founding_goal || 50) * 2}`} className={inputCls} />
            <button data-testid="stretch-goal-save" disabled={raising} onClick={raiseGoal} className="bg-[#D90429] text-white px-5 uppercase tracking-[0.12em] text-xs font-bold hover:bg-[#b0031f] transition-colors duration-200 disabled:opacity-60 whitespace-nowrap">{raising ? "Saving…" : "Set Goal"}</button>
          </div>
        </div>
      )}

      {/* Edit form */}
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2"><label className={labelCls}>Chapter Name</label><input data-testid="my-chapter-name" name="name" value={form.name} onChange={onChange} className={inputCls} /></div>
        <div><label className={labelCls}>City / Region</label><input data-testid="my-chapter-city" name="city" value={form.city} onChange={onChange} className={inputCls} /></div>
        <div>
          <label className={labelCls}>Status</label>
          <select data-testid="my-chapter-status" name="status" value={form.status} onChange={onChange} className={inputCls}>
            <option value="Forming">Forming</option>
            <option value="Active">Active</option>
          </select>
        </div>
        <div><label className={labelCls}>Contact Email</label><input data-testid="my-chapter-email" name="contact_email" value={form.contact_email} onChange={onChange} className={inputCls} /></div>
        <div><label className={labelCls}>Contact Phone</label><input data-testid="my-chapter-phone" name="contact_phone" value={form.contact_phone} onChange={onChange} className={inputCls} /></div>
        <div className="sm:col-span-2"><label className={labelCls}>External Link (optional)</label><input data-testid="my-chapter-link" name="link" value={form.link} onChange={onChange} placeholder="https://…" className={inputCls} /></div>
      </div>
      <button data-testid="my-chapter-save" disabled={saving} onClick={save} className="mt-5 inline-flex items-center gap-2 bg-[#0A1128] text-white px-8 py-3 uppercase tracking-[0.15em] text-xs font-bold hover:bg-[#1a2547] transition-colors duration-200 disabled:opacity-60">
        {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : <><Save className="w-4 h-4" /> Save Changes</>}
      </button>

      {/* Founding members */}
      <div className="mt-8 pt-6 border-t border-slate-100">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-[#D90429]" />
            <h3 className="font-display text-xl uppercase">Founding Members ({members.length})</h3>
          </div>
          {members.length > 0 && (
            <button data-testid="my-chapter-export-csv" onClick={exportCsv} className="inline-flex items-center gap-2 bg-[#D90429] text-white px-5 py-2.5 uppercase tracking-[0.12em] text-xs font-bold hover:bg-[#b0031f] transition-colors duration-200">
              <Download className="w-4 h-4" /> Export CSV
            </button>
          )}
        </div>
        {members.length === 0 ? (
          <p className="text-slate-400 py-4 text-sm">No founding members yet. Share your chapter link above to start recruiting.</p>
        ) : (
          <div className="space-y-2">
            {members.map((m) => (
              <div key={m.id} data-testid="my-chapter-member-row" className="flex items-center justify-between border border-slate-100 p-3 text-sm gap-3">
                <span className="font-semibold">{m.name}</span>
                <span className="text-slate-400 truncate">{m.email}{m.zip_code ? ` · ${m.zip_code}` : ""}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Announcements */}
      <div className="mt-8 pt-6 border-t border-slate-100" data-testid="my-chapter-announcements">
        <div className="flex items-center gap-2 mb-4">
          <Megaphone className="w-5 h-5 text-[#D90429]" />
          <h3 className="font-display text-xl uppercase">Announcements</h3>
        </div>
        <p className="text-sm text-slate-500 mb-4">
          Post an update and we'll email it to all {members.length} of your founding member{members.length !== 1 ? "s" : ""}.
        </p>
        <div className="space-y-4">
          <input
            data-testid="announcement-subject"
            value={ann.subject}
            onChange={(e) => setAnn({ ...ann, subject: e.target.value })}
            placeholder="Subject — e.g. First meeting this Saturday"
            className={inputCls}
          />
          <textarea
            data-testid="announcement-message"
            value={ann.message}
            onChange={(e) => setAnn({ ...ann, message: e.target.value })}
            rows={4}
            placeholder="Write your update to founding members…"
            className={`${inputCls} resize-none`}
          />
          <button
            data-testid="announcement-send"
            disabled={sending}
            onClick={sendAnnouncement}
            className="inline-flex items-center gap-2 bg-[#D90429] text-white px-8 py-3 uppercase tracking-[0.15em] text-xs font-bold hover:bg-[#b0031f] transition-colors duration-200 disabled:opacity-60"
          >
            {sending ? <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</> : <><Send className="w-4 h-4" /> Send Update</>}
          </button>
        </div>

        {announcements.length > 0 && (
          <div className="mt-6 space-y-3">
            <p className="text-xs uppercase tracking-wider text-slate-400">Sent History</p>
            {announcements.map((a) => (
              <div key={a.id} data-testid="announcement-row" className="border border-slate-100 p-4">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <span className="font-semibold">{a.subject}</span>
                  <span className="text-xs text-slate-400">{new Date(a.created_at).toLocaleDateString()} · {a.recipient_count} sent</span>
                </div>
                <p className="text-sm text-slate-500 mt-1 whitespace-pre-line line-clamp-3">{a.message}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
};
